export const AUTOCOMPLETE_PREFIX = '@'
export const AUTOCOMPLETE_AGENT_PREFIX = '@agent:'

// Types
type Repository = {
  name: string
  owner: string
  visibility: string
  nameWithOwner: string
}

type Issue = {
  number: number
  title: string
  state: string
  repository: string
}

type PullRequest = {
  number: number
  title: string
  state: string
  repository: string
}

type Discussion = {
  number: number
  title: string
  state: string
  repository: string
}

type FetchableItemType = Repository | Issue | PullRequest | Discussion

let abortController: AbortController

const abortPreviousRequests = () => {
  // If we had any previous requests that were still pending, terminate them and start a new one
  // This avoids race conditions with results coming back out of order
  abortController?.abort()
  abortController = new AbortController()
}

export const getRepos = async (query: string): Promise<Repository[]> => {
  const endpoint = new URL(`/copilot/chat/autocomplete/repositories`, window.location.origin)
  const params = new URLSearchParams()
  params.set('q', query)
  endpoint.search = params.toString()

  return (await fetchItems<Repository>(endpoint)) ?? []
}

export const getIssues = async (repo: string, query: string): Promise<Issue[]> => {
  const endpoint = new URL(`/copilot/chat/autocomplete/issues`, window.location.origin)
  const params = new URLSearchParams()
  params.set('repo', repo)
  params.set('q', query)
  endpoint.search = params.toString()
  return (await fetchItems<Issue>(endpoint)) ?? []
}

export const getPullRequests = async (repo: string, query: string): Promise<PullRequest[]> => {
  const endpoint = new URL(`/copilot/chat/autocomplete/pulls`, window.location.origin)
  const params = new URLSearchParams()
  params.set('repo', repo)
  params.set('q', query)
  endpoint.search = params.toString()
  return (await fetchItems<PullRequest>(endpoint)) ?? []
}

export const getDiscussions = async (repo: string, query: string): Promise<Discussion[]> => {
  const endpoint = new URL(`/copilot/chat/autocomplete/discussions`, window.location.origin)
  const params = new URLSearchParams()
  params.set('repo', repo)
  params.set('q', query)
  endpoint.search = params.toString()
  return (await fetchItems<Discussion>(endpoint)) ?? []
}

export const fetchItems = async <T extends FetchableItemType>(endpoint: URL) => {
  let response
  abortPreviousRequests()
  try {
    response = await fetch(endpoint, {
      method: 'GET',
      mode: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: abortController.signal,
    })
  } catch (error) {
    // @ts-expect-error catch blocks are bound to `unknown` so we need to validate the type before using it
    if (error.name !== 'AbortError') {
      throw error
    }
  }

  if (!response || !response.ok) {
    return null
  }
  const json = await response.json()
  return json as T[] | null
}
